import React, { createContext, useState, useContext, useRef } from 'react';
import simplify from 'simplify-geojson';

// Create the context
const MapContext = createContext();

// Create the provider
export const MapProvider = ({ children }) => {
  const [geojsonData, setGeojsonData] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [simplifyTolerance, setSimplifyTolerance] = useState(0.0002);
  const mapRef = useRef(null);
  const drawnLayerRef = useRef(null);
  const featureLayersRef = useRef({});
  
  // Load GeoJSON data
  const loadGeoJSON = (data) => {
    setGeojsonData(data);
    setSelectedFeature(null);
  };
  
  // Select a feature for editing
  const selectFeature = (feature, layerId) => {
    setSelectedFeature({ feature, layerId });
  };
  
  // Begin editing a feature
  const startEditing = () => {
    if (selectedFeature) {
      setIsEditing(true);
      return true;
    }
    return false;
  };
  
  // Finish editing and save changes
  const finishEditing = (updatedGeometry) => {
    if (selectedFeature && isEditing) {
      // Update the geometry in the geojsonData
      const updatedData = { ...geojsonData };
      const featureIndex = updatedData.features.findIndex(
        f => f === selectedFeature.feature
      );
      
      if (featureIndex !== -1 && updatedGeometry) {
        updatedData.features[featureIndex].geometry = updatedGeometry;
        setGeojsonData(updatedData);
      }
      
      setIsEditing(false);
      return true;
    }
    return false;
  };
  
  // Update feature properties
  const updateFeatureProperties = (properties) => {
    if (selectedFeature) {
      const updatedData = { ...geojsonData };
      const featureIndex = updatedData.features.findIndex(
        f => f === selectedFeature.feature
      );
      
      if (featureIndex !== -1) {
        updatedData.features[featureIndex].properties = {
          ...updatedData.features[featureIndex].properties,
          ...properties
        };
        
        setGeojsonData(updatedData);
        setSelectedFeature({
          ...selectedFeature,
          feature: updatedData.features[featureIndex]
        });
        
        return true;
      }
    }
    return false;
  };
  
  // Simplify the geometry of the selected feature
  const simplifyGeometry = () => {
    if (selectedFeature && !isEditing) {
      const updatedData = { ...geojsonData };
      const featureIndex = updatedData.features.findIndex(
        f => f === selectedFeature.feature
      );
      
      if (featureIndex !== -1) {
        // Get the current vertex count before simplification
        const beforeCount = countVertices(selectedFeature.feature.geometry);
        
        // Create a single feature GeoJSON for simplification
        const featureToSimplify = {
          type: 'FeatureCollection',
          features: [{ ...updatedData.features[featureIndex] }]
        };
        
        // Log for debugging
        console.log('Simplifying with tolerance:', simplifyTolerance);
        
        try {
          // Apply simplification with the current tolerance value
          // Note: simplify-geojson expects the tolerance as a number between 0 and 1
          const simplified = simplify(featureToSimplify, simplifyTolerance);
          
          if (simplified && simplified.features && simplified.features[0]) {
            // Get the updated geometry
            const newGeometry = simplified.features[0].geometry;
            
            // Calculate the after count
            const afterCount = countVertices(newGeometry);
            
            // Only update if simplification actually reduced vertices
            if (afterCount < beforeCount) {
              // Update the feature with simplified geometry
              updatedData.features[featureIndex].geometry = newGeometry;
              
              // Update state
              setGeojsonData(updatedData);
              setSelectedFeature({
                ...selectedFeature,
                feature: updatedData.features[featureIndex]
              });
              
              return {
                success: true,
                before: beforeCount,
                after: afterCount
              };
            } else {
              console.log('Simplification did not reduce vertices. Try a larger tolerance value.');
              return {
                success: false,
                message: 'No reduction in vertices. Try a larger tolerance value.'
              };
            }
          }
        } catch (error) {
          console.error('Error during simplification:', error);
          return { success: false, error: error.message };
        }
      }
    }
    return { success: false };
  };
  
  // Delete the selected feature
  const deleteFeature = () => {
    if (selectedFeature) {
      const updatedData = { ...geojsonData };
      const featureIndex = updatedData.features.findIndex(
        f => f === selectedFeature.feature
      );
      
      if (featureIndex !== -1) {
        updatedData.features.splice(featureIndex, 1);
        setGeojsonData(updatedData);
        setSelectedFeature(null);
        return true;
      }
    }
    return false;
  };
  
  // Helper function to count vertices in a geometry
  const countVertices = (geometry) => {
    if (!geometry || !geometry.coordinates) return 0;
    
    let count = 0;
    
    if (geometry.type === 'Polygon') {
      geometry.coordinates.forEach(ring => {
        count += ring.length;
      });
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygon => {
        polygon.forEach(ring => {
          count += ring.length;
        });
      });
    }
    
    return count;
  };
  
  // Define colors for different DUNs
  const getColorForDUN = (dunCode) => {
    // Helper function to get color for DUN based on DUN_CODE
    const code = dunCode || '';
    
    const colorMap = {
      'N.13': '#ff7f00', // Orange - KUALA SEPETANG
      'N.14': '#377eb8', // Blue - CHANGKAT JERING
      'N.15': '#4daf4a', // Green - TRONG
      'N.16': '#e41a1c', // Red 
      'N.17': '#984ea3', // Purple
      'N.18': '#ffff33', // Yellow
      'N.19': '#a65628', // Brown
      'N.20': '#f781bf'  // Pink
    };
    
    return colorMap[code] || '#999999'; // Default gray for unknown codes
  };
  
  return (
    <MapContext.Provider 
      value={{
        geojsonData,
        setGeojsonData,
        selectedFeature,
        setSelectedFeature,
        isEditing,
        setIsEditing,
        simplifyTolerance,
        setSimplifyTolerance,
        mapRef,
        drawnLayerRef,
        featureLayersRef,
        loadGeoJSON,
        selectFeature,
        startEditing,
        finishEditing,
        updateFeatureProperties,
        simplifyGeometry,
        deleteFeature,
        getColorForDUN
      }}
    >
      {children}
    </MapContext.Provider>
  );
};

// Custom hook to use the map context
export const useMapContext = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapContext must be used within a MapProvider');
  }
  return context;
};

export default MapContext;
